import { Button, ModalDialog, Select } from "@rothoblaas/design-system";
import { NoticeFormField as FormField } from "../notices/NoticeFormField";
import { FC, useContext, useRef, useState } from "react";
import { Controller, useFormContext } from "react-hook-form";
import NotificationDispatch, {
  showErrorNotification,
  showSuccessNotification,
} from "../../../../../context/notificationContext";
import useI18n from "../../../../../hooks/useTranslations";
import { isConflict } from "../../../../../http/errors";
import type { CustomMaterialGrade, MaterialGradeLight } from "../../../../../http/lock_calculator/types";
import { useCustomMaterials, useCustomMaterialsApi } from "../../../../../http/lock_calculator/useCustomMaterials";
import { useLockCalculatorCatalog } from "../../hooks/useLockCalculatorCatalog";
import styles from "../../LockCalculator.module.css";
import type { LockCalculatorFormValues } from "../../model/form";
import { CustomMaterialDialog } from "./CustomMaterialDialog";

type MaterialFieldName = "headerMember.materialGradeId" | "joistMember.materialGradeId";

const MEMBER_FIELDS: MaterialFieldName[] = ["headerMember.materialGradeId", "joistMember.materialGradeId"];

type DialogState = { mode: "create" } | { mode: "edit"; item: CustomMaterialGrade };

interface MaterialGradeFieldProps {
  name: MaterialFieldName;
  label?: string;
  helpText?: string;
  readOnly?: boolean;
  onOpenHelp?: (text: string, image?: string) => void;
}

/**
 * The material select of the header or the joist. Both list the catalog and the user's own materials;
 * from here the user creates a custom material, or edits / deletes the one currently selected.
 */
export const MaterialGradeField: FC<MaterialGradeFieldProps> = ({ name, label, helpText, readOnly = false, onOpenHelp }) => {
  const i18n = useI18n();
  const t = i18n.translation;
  const text = t.lockCalculatorModule.customMaterial;
  const dispatch = useContext(NotificationDispatch);
  const {
    control,
    getValues,
    setValue,
    formState: { errors },
  } = useFormContext<LockCalculatorFormValues>();
  const { materialGrades, isLoadingMaterialGrades, isAdmin, calculationId } = useLockCalculatorCatalog();
  const aborter = useRef(new AbortController());
  const { data: library } = useCustomMaterials(aborter.current.signal, isAdmin);
  const customApi = useCustomMaterialsApi(isAdmin);

  const [dialog, setDialog] = useState<DialogState | undefined>();
  const [isConfirmingDelete, setIsConfirmingDelete] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const optionLabel = (grade: MaterialGradeLight) =>
    grade.isCustom ? `${grade.description} (${text.badge})` : grade.description;

  // The library entry carries the usage count; fall back to the select option until it loads.
  const asCustomItem = (grade: MaterialGradeLight): CustomMaterialGrade =>
    library?.items.find((item) => item.id === grade.id) ?? { ...grade, usageCount: 0 };

  const deleteSelected = async (grade: MaterialGradeLight) => {
    setIsDeleting(true);
    try {
      await customApi.remove(grade.id, calculationId);
      // The server already dropped it from this calculation; the form follows.
      MEMBER_FIELDS.forEach((field) => {
        if (getValues(field) === grade.id) setValue(field, null, { shouldDirty: true });
      });
      dispatch(showSuccessNotification(t));
      setIsConfirmingDelete(false);
    } catch (err) {
      // 409: another calculation still uses the material.
      dispatch(showErrorNotification(i18n, err, isConflict(err) ? text.deleteBlocked : undefined));
    } finally {
      setIsDeleting(false);
    }
  };

  const fieldError = name === "headerMember.materialGradeId" ? errors.headerMember?.materialGradeId : errors.joistMember?.materialGradeId;

  return (
    <Controller
      name={name}
      control={control}
      render={({ field }) => {
        const selected = materialGrades.find((grade) => grade.id === field.value);
        const selectedCustom = selected?.isCustom ? selected : undefined;

        return (
          <>
            <FormField name={name}
              label={label}
              error={fieldError}
              showHelp={{ tooltip: helpText, onOpen: () => onOpenHelp?.(helpText ?? "") }}
            >
              {({ labelId, isInvalid }) => (
                <>
                  <Select<MaterialGradeLight, false>
                    inputId={labelId}
                    value={selected ?? null}
                    options={materialGrades}
                    getOptionValue={(grade) => grade.id}
                    getOptionLabel={optionLabel}
                    placeholder={t.lockCalculatorModule.selectPlaceholder}
                    isLoading={isLoadingMaterialGrades}
                    isDisabled={readOnly}
                    isInvalid={isInvalid}
                    onChange={(grade) => field.onChange(grade?.id ?? null)}
                  />
                  {!readOnly && (
                    <div className={styles.fieldActions}>
                      <Button variant="secondary" onClick={() => setDialog({ mode: "create" })}>
                        {text.create}
                      </Button>
                      {selectedCustom && (
                        <>
                          <Button variant="secondary" onClick={() => setDialog({ mode: "edit", item: asCustomItem(selectedCustom) })}>
                            {t.common.edit}
                          </Button>
                          <Button variant="secondary" onClick={() => setIsConfirmingDelete(true)}>
                            {text.remove}
                          </Button>
                        </>
                      )}
                    </div>
                  )}
                </>
              )}
            </FormField>

            {dialog && (
              <CustomMaterialDialog
                item={dialog.mode === "edit" ? dialog.item : undefined}
                baseOptions={materialGrades}
                onClose={() => setDialog(undefined)}
                onSaved={(id) => field.onChange(id)}
              />
            )}

            {isConfirmingDelete && selectedCustom && (
              <ModalDialog
                onClose={() => setIsConfirmingDelete(false)}
                title={text.remove}
                isSubmitting={isDeleting}
                buttons={[
                  <Button key={0} onClick={() => setIsConfirmingDelete(false)} variant="secondary">
                    {t.common.cancel}
                  </Button>,
                  <Button key={1} onClick={() => deleteSelected(selectedCustom)}>
                    {t.common.yes}
                  </Button>,
                ]}
              >
                {text.deleteConfirm}
              </ModalDialog>
            )}
          </>
        );
      }}
    />
  );
};
